/**
 * CCI module entitlements — source of truth for subscription feature gates.
 * See docs/CCI_MODULE_ENTITLEMENTS.md.
 */
/** Canonical CCI software modules stored on subscriptions.features. */
export const CCI_MODULE_KEYS = [
    'cci_operations',
    'cci_vision',
    'spatial_connect',
    'spatial_uwb',
];
/** Optional add-on modules (CCI Operations is included in base price). */
export const STRIPE_MODULE_FEATURE_KEYS = [
    'cci_vision',
    'spatial_connect',
    'spatial_uwb',
];
/** Keys shown in enterprise package UI — add-on modules only (base CCI Operations is implicit). */
export const ENTERPRISE_ENTITLEMENT_FEATURE_KEYS = STRIPE_MODULE_FEATURE_KEYS;
/** Module keys resolved into GET /api/tenant/features/capabilities → modules. */
export const TENANT_FEATURE_KEYS = [...CCI_MODULE_KEYS, 'axon_gateways'];
export function getMaxAxonGateways(limits) {
    return limits?.maxAxonGateways ?? 0;
}
export function hasAxonGatewayCapacity(limits) {
    return getMaxAxonGateways(limits) > 0;
}
function readBooleanFlag(features, key) {
    const value = features[key];
    if (value === true)
        return true;
    if (value === false)
        return false;
    return undefined;
}
/**
 * Resolve whether a CCI module is entitled for a tenant subscription.
 */
export function isCciModuleEnabled(moduleKey, features, options) {
    const featureMap = features ?? {};
    if (readBooleanFlag(featureMap, moduleKey) === false) {
        return false;
    }
    if (readBooleanFlag(featureMap, moduleKey) === true) {
        return true;
    }
    // CCI Operations is the base software module — included with any active subscription.
    if (moduleKey === 'cci_operations' && options?.hasSubscriptionAccess) {
        return true;
    }
    return false;
}
/**
 * Resolve any tenant-facing feature key (module or computed capacity).
 */
export function isTenantFeatureEnabled(featureKey, features, limits, options) {
    if (featureKey === 'axon_gateways') {
        return hasAxonGatewayCapacity(limits);
    }
    if (CCI_MODULE_KEYS.includes(featureKey)) {
        return isCciModuleEnabled(featureKey, features, options);
    }
    return false;
}
/** Kill-switch flag keys to check for a tenant feature. */
export function getKillSwitchFlagKeys(featureKey) {
    return [featureKey];
}
/** UI hint for optional source-filter chips when both spatial modules are enabled. Not used for read API filtering. */
export function getSpatialPositionSourceFilter(features, options) {
    const hasConnect = isCciModuleEnabled('spatial_connect', features, options);
    const hasUwb = isCciModuleEnabled('spatial_uwb', features, options);
    if (hasConnect && hasUwb)
        return 'all';
    if (hasConnect)
        return 'vendor';
    if (hasUwb)
        return 'uwb';
    return null;
}
/** Provider platform roles bypass tenant module gates. */
export function isProviderUserRole(role) {
    return typeof role === 'string' && role.startsWith('provider_');
}
export function buildTenantRouteCapabilities(modules) {
    const spatialAny = modules.spatial_connect === true || modules.spatial_uwb === true;
    return {
        spatialOverview: spatialAny,
        nodeCommandCenter: spatialAny,
        nodeFleetHealth: spatialAny,
        satelliteHealth: modules.spatial_uwb === true,
        cleanroomLayout: spatialAny,
        axonSetup: modules.axon_gateways === true,
        scanTemplates: modules.cci_vision === true,
        waferIdAcquire: modules.cci_vision === true,
        waferDataRefresh: modules.cci_vision === true,
        audits: modules.cci_vision === true,
        conflictResolution: modules.cci_vision === true,
    };
}
export function getCreatableJobTypes(modules) {
    const types = [];
    if (modules.cci_vision)
        types.push('container_slot_scan');
    if (modules.batchWaferTransfer)
        types.push('batch_wafer_transfer');
    return types;
}
/** Parse Stripe product metadata into subscription.features module keys. */
export function parseSubscriptionFeaturesFromMetadata(metadata) {
    const features = {
        cci_operations: metadata.feature_cci_operations !== 'false',
    };
    for (const key of CCI_MODULE_KEYS) {
        if (metadata[`feature_${key}`] === 'true') {
            features[key] = true;
        }
    }
    return features;
}
//# sourceMappingURL=enterprise-entitlements.js.map